import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  X,
  Send,
  Paperclip,
  MessageSquare,
  Target,
  Plus,
  Trash2,
  ChevronLeft,
  Loader2,
  User,
  Clock,
  SlidersHorizontal,
} from 'lucide-react';
import { AIIcon } from './AIIcon';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  closeChat,
  sendMessage,
  addUserMessage,
  fetchConversations,
  fetchConversation,
  deleteConversation,
  startNewConversation,
  clearStreamingContent,
  clearActionPreview,
  clearStreamingTrackData,
  createFullTrack,
  showQuestionnaireForMessage,
  hideQuestionnaire,
} from '@/store/slices/aiSlice';
import type { TrackGenerationParams } from '@/store/slices/aiSlice';
import { cn } from '@/utils/helpers';
import { TaskPreviewModal } from './TaskPreviewModal';
import { StreamingTrackPreview } from './StreamingTrackPreview';
import { AITrackPreviewModal, type AITrackData } from './AITrackPreviewModal';
import { TrackQuestionnaire } from './TrackQuestionnaire';
import { AIResponseRenderer } from './AIResponseRenderer';
type TabType = 'chat' | 'plans';

const PROMPT_SUGGESTIONS = [
  'What should I do today?',
  'Show my progress',
  'Create a learning track',
  'Reschedule overdue tasks',
  'How are my habits?',
  'Summarize my week',
];

export function AIChatModal() {
  const dispatch = useAppDispatch();
  const {
    isOpen,
    messages,
    conversations,
    currentConversationId,
    isTyping,
    isSending,
    health,
    streamingContent,
    actionPreview,
    streamingTrackData,
    isStreamingTrack,
    showQuestionnaire,
    pendingMessage,
  } = useAppSelector((state) => state.ai);

  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [inputValue, setInputValue] = useState('');
  const [showConversations, setShowConversations] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showPromptBuilder, setShowPromptBuilder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load conversations, briefing, and suggestions on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchConversations());
    }
  }, [isOpen, dispatch]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, streamingContent]);

  // Focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const isTrackCreationIntent = (text: string): boolean => {
    const patterns = [
      // Only match when user EXPLICITLY says track/roadmap/curriculum/learning path/study plan
      /\b(create|build|make|generate|design|start)\b.*\b(track|roadmap|curriculum|learning\s*path|study\s*plan|course\s+structure)\b/i,
    ];
    return patterns.some((p) => p.test(text));
  };

  const handleSend = async () => {
    if (!inputValue.trim() && !selectedImage) return;

    const message = inputValue.trim();
    setInputValue('');

    // Detect track creation intent (only for text messages without images)
    if (!selectedImage && isTrackCreationIntent(message)) {
      // Show user message in chat, then show questionnaire
      dispatch(addUserMessage({ content: message }));
      dispatch(showQuestionnaireForMessage(message));
      return;
    }

    // Clear any previous streaming content and track data
    dispatch(clearStreamingContent());
    dispatch(clearStreamingTrackData());

    // Add user message to UI
    dispatch(addUserMessage({ content: message, imageUrl: selectedImage || undefined }));

    // Send to API with streaming
    const result = await dispatch(
      sendMessage({
        message,
        conversationId: currentConversationId || undefined,
        imageBase64: selectedImage || undefined,
      })
    );

    if (sendMessage.rejected.match(result)) {
      toast.error('Something went wrong. Please try again.');
    }

    // Clear streaming content after completion (keep track data for preview modal)
    dispatch(clearStreamingContent());
    setSelectedImage(null);
  };

  const handleQuestionnaireSubmit = async (params: TrackGenerationParams) => {
    const message = pendingMessage;
    if (!message) return;

    dispatch(hideQuestionnaire());

    // Clear any previous streaming content and track data
    dispatch(clearStreamingContent());
    dispatch(clearStreamingTrackData());

    // Send to API with streaming + trackParams
    const result = await dispatch(
      sendMessage({
        message,
        conversationId: currentConversationId || undefined,
        trackParams: params,
      })
    );

    if (sendMessage.rejected.match(result)) {
      toast.error('Something went wrong. Please try again.');
    }

    // Clear streaming content after completion
    dispatch(clearStreamingContent());
  };

  const handleQuestionnaireCancel = () => {
    dispatch(hideQuestionnaire());
  };

  // Handle choice button clicks from AIResponseRenderer
  const handleChoiceMessage = async (message: string) => {
    if (!message.trim()) return;

    dispatch(clearStreamingContent());
    dispatch(clearStreamingTrackData());

    dispatch(addUserMessage({ content: message }));

    const result = await dispatch(
      sendMessage({
        message,
        conversationId: currentConversationId || undefined,
      })
    );

    if (sendMessage.rejected.match(result)) {
      toast.error('Something went wrong. Please try again.');
    }

    dispatch(clearStreamingContent());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        setSelectedImage(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectConversation = (convId: string) => {
    dispatch(fetchConversation(convId));
    setShowConversations(false);
  };

  const handleNewChat = () => {
    dispatch(startNewConversation());
    setShowConversations(false);
  };

  const handleDeleteConversation = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    dispatch(deleteConversation(convId));
  };

  const isConnected = health?.ollama?.connected ?? false;

  if (!isOpen && !actionPreview) return null;

  const chatModal = isOpen ? createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => dispatch(closeChat())}
        />

        {/* Modal */}
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={cn(
            'relative w-full max-w-[500px]',
            'h-[600px] max-h-[85vh]',
            'bg-white dark:bg-gray-900',
            'rounded-2xl',
            'shadow-2xl ring-[5px] ring-gray-200/80 dark:ring-gray-700/80',
            'overflow-hidden flex flex-col'
          )}
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {showConversations ? (
                  <button
                    onClick={() => setShowConversations(false)}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                ) : (
                  <AIIcon size={40} static />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {showConversations ? 'Conversations' : 'AI Assistant'}
                  </h2>
                  {!showConversations && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <span
                        className={cn(
                          'w-2 h-2 rounded-full',
                          isConnected ? 'bg-green-500' : 'bg-red-500'
                        )}
                      />
                      {isConnected ? 'Connected' : 'Offline'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!showConversations && (
                  <button
                    onClick={() => setShowConversations(true)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="View conversations"
                  >
                    <MessageSquare className="w-5 h-5 text-gray-500" />
                  </button>
                )}
                <button
                  onClick={() => dispatch(closeChat())}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            {!showConversations && (
              <div className="flex gap-1 mt-3 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
                {[
                  { id: 'chat', label: 'Chat', icon: MessageSquare },
                  { id: 'plans', label: 'Plans', icon: Target },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all',
                      activeTab === tab.id
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {showConversations ? (
              // Conversations list
              <div className="h-full overflow-y-auto p-4 space-y-2">
                <button
                  onClick={handleNewChat}
                  className="w-full p-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <Plus className="w-5 h-5" />
                  New Conversation
                </button>

                {conversations.map((conv) => (
                  <motion.div
                    key={conv.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      'p-3 rounded-xl cursor-pointer transition-all group',
                      currentConversationId === conv.id
                        ? 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                    )}
                    onClick={() => handleSelectConversation(conv.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {conv.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {conv.lastMessage || 'No messages'}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        className="p-1.5 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(conv.createdAt).toLocaleDateString()}
                    </p>
                  </motion.div>
                ))}

                {conversations.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No conversations yet</p>
                    <p className="text-sm">Start a new chat to begin</p>
                  </div>
                )}
              </div>
            ) : activeTab === 'chat' ? (
              // Chat messages
              <div className="h-full overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && !isTyping && (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <AIIcon size={64} className="mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      How can I help you today?
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm">
                      Ask me about your tracks, tasks, habits, or let me help you create a learning plan.
                    </p>
                  </div>
                )}

                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={cn(
                      'flex gap-3',
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {msg.role === 'assistant' && (
                      <AIIcon size={32} className="flex-shrink-0" static />
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-2xl px-4 py-2.5',
                        msg.role === 'user'
                          ? 'bg-gray-800 dark:bg-gray-700 text-white rounded-br-sm'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-sm'
                      )}
                    >
                      {msg.imageUrl && (
                        <div className="mb-2 rounded-lg overflow-hidden">
                          <img
                            src={`data:image/jpeg;base64,${msg.imageUrl}`}
                            alt="Uploaded"
                            className="max-w-full h-auto"
                          />
                        </div>
                      )}
                      {msg.role === 'assistant' ? (
                        <AIResponseRenderer content={msg.content} onSendMessage={handleChoiceMessage} />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      )}
                      {msg.responseTimeMs && (
                        <p className="text-xs opacity-60 mt-1">
                          {(msg.responseTimeMs / 1000).toFixed(1)}s
                        </p>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </div>
                    )}
                  </motion.div>
                ))}

                {/* Track creation questionnaire */}
                {showQuestionnaire && (
                  <TrackQuestionnaire
                    onSubmit={handleQuestionnaireSubmit}
                    onCancel={handleQuestionnaireCancel}
                  />
                )}

                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3"
                  >
                    <AIIcon size={32} className="flex-shrink-0" />
                    <div className={cn(
                      'max-w-[85%]',
                      !isStreamingTrack && 'bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3'
                    )}>
                      {/* Show StreamingTrackPreview when track creation is detected */}
                      {isStreamingTrack && streamingTrackData ? (
                        <StreamingTrackPreview
                          data={streamingTrackData}
                          isStreaming={true}
                        />
                      ) : streamingContent ? (
                        <div className="text-gray-800 dark:text-gray-200 text-sm">
                          <AIResponseRenderer content={streamingContent} isStreaming={true} onSendMessage={handleChoiceMessage} />
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400">Thinking</span>
                          <span className="flex gap-1">
                            {[0, 1, 2].map((i) => (
                              <span
                                key={i}
                                className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse"
                                style={{ animationDelay: `${i * 200}ms` }}
                              />
                            ))}
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>
            ) : (
              // Plans tab
              <div className="h-full overflow-y-auto p-4">
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">AI Plans Coming Soon</p>
                  <p className="text-sm mt-1">
                    Create long-term learning plans with AI assistance
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Input area (only in chat tab) */}
          {!showConversations && activeTab === 'chat' && (
            <div className="flex-shrink-0 p-4 border-t border-gray-100 dark:border-gray-800">
              {/* Prompt builder panel */}
              <AnimatePresence>
                {showPromptBuilder && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {PROMPT_SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => {
                            setInputValue(s);
                            setShowPromptBuilder(false);
                            inputRef.current?.focus();
                          }}
                          className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Input container */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 overflow-hidden focus-within:border-gray-400 dark:focus-within:border-gray-500 transition-colors">
                {/* Image preview */}
                {selectedImage && (
                  <div className="px-4 pt-3 relative inline-block">
                    <img
                      src={`data:image/jpeg;base64,${selectedImage}`}
                      alt="Selected"
                      className="h-20 w-auto rounded-lg"
                    />
                    <button
                      onClick={() => setSelectedImage(null)}
                      className="absolute top-1 right-2 w-5 h-5 rounded-full bg-gray-900/70 text-white flex items-center justify-center hover:bg-gray-900/90 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Textarea */}
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isConnected
                      ? 'Ask me anything...'
                      : 'AI is offline. Start Ollama to chat.'
                  }
                  disabled={!isConnected || isSending}
                  className={cn(
                    'w-full px-4 pt-3 pb-2 bg-transparent resize-none',
                    'text-sm text-gray-900 dark:text-white',
                    'placeholder-gray-400 dark:placeholder-gray-500',
                    'focus:outline-none',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'min-h-[80px] max-h-40'
                  )}
                  rows={3}
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageSelect}
                />

                {/* Bottom row inside container */}
                <div className="flex items-center justify-between px-3 pb-2">
                  <button
                    onClick={() => setShowPromptBuilder(!showPromptBuilder)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      showPromptBuilder
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    )}
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Prompt builder
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSending}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Send button */}
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleSend}
                  disabled={(!inputValue.trim() && !selectedImage) || !isConnected || isSending}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors',
                    inputValue.trim() || selectedImage
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  ) : null;

  const handleTrackConfirm = async (trackData: AITrackData) => {
    try {
      await dispatch(createFullTrack(trackData)).unwrap();
      dispatch(clearActionPreview());
      dispatch(fetchConversations());
    } catch (error) {
      console.error('Failed to create track:', error);
    }
  };

  return (
    <>
      {chatModal}

      {/* AI Track Preview Modal (shown when AI suggests creating tracks) */}
      {actionPreview && actionPreview.type === 'create_track' && (
        <AITrackPreviewModal
          isOpen={!!actionPreview}
          onClose={() => dispatch(clearActionPreview())}
          onConfirm={handleTrackConfirm}
          initialData={actionPreview.data as AITrackData}
        />
      )}

      {/* Task Preview Modal (shown for other action types like scheduling) */}
      {actionPreview && actionPreview.type !== 'create_track' && (
        <TaskPreviewModal
          isOpen={!!actionPreview}
          onClose={() => dispatch(clearActionPreview())}
          actionPreview={actionPreview}
          onSuccess={() => {
            dispatch(clearActionPreview());
            // Refresh conversations to show updated content
            dispatch(fetchConversations());
          }}
        />
      )}
    </>
  );
}

export default AIChatModal;
